import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import dayjs from "dayjs";

import Spinner from "../../assets/icons/Spinner";
import { login } from "../auth/store/actionCreators";
import { authSelectors } from "../auth/store/authReducer";
import LineChartIcon from "../../assets/icons/LineChartIcon";
import CalendarIcon from "../../assets/icons/CalendarIcon";
import capitalize from "lodash/capitalize";
import CreditCardIcon from "../../assets/icons/CreditCardIcon";
import { accountSelectors } from "./store/accountReducer";
import {
  loadAccountOrders,
  upgradeSubscription,
  changeTrialPlan,
  cancelSubscription,
} from "./store/actionCreators";

import Message from "../../i18n/Message";
import PlanLimits from "./PlanLimits";
import { selfServePlans } from "../shared/pricingLimits";
import label from "../../i18n/label";
import CheckIcon from "../../assets/icons/CheckIcon";
import ConfirmUpgradeDialog from "./ConfirmUpgradeDialog";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import ChatbotLayout from "../chatbot/shared/ChatbotLayout";

import "./SubscriptionPage.scss";

// Self-serve plans only (Free retired; Personalized/Enterprise is contact-sales).
const upgradeOptions = selfServePlans;

const SubscriptionPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const user = useSelector(authSelectors.getUser);
  const userMins = useSelector(authSelectors.getMinutesSinceLoaded);
  const orders = useSelector(accountSelectors.getOrders);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [inProgressPlan, setInProgressPlan] = useState(null);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (null === user || (userMins && userMins > 1)) {
      dispatch(login());
    }
  }, [dispatch, user, userMins]);

  useEffect(() => {
    if (!orders) {
      dispatch(loadAccountOrders(intl));
    }
  }, [dispatch, orders, intl]);

  const onUpgradeConfirmed = () => {
    const target = pendingUpgrade;
    setInProgressPlan(target);
    setPendingUpgrade(null);

    // Reload so every derived limit/badge across the app reflects the new plan
    // (consistent with the in-place upgrade-from-hint flow).
    const onDone = () => window.location.reload();
    const onErr = () => setInProgressPlan(null);

    if (sub.trialInProgress) {
      // Still trialing (no card yet) — switch the trial plan for free.
      dispatch(changeTrialPlan(intl, target, onDone, onErr));
    } else if (sub.paid) {
      // Active paid subscription — change in place (FastSpring proration).
      dispatch(upgradeSubscription(intl, target, onDone, onErr));
    } else {
      // Expired trial with no card — open the FastSpring checkout for the chosen plan.
      setInProgressPlan(null);
      window.showPaymentWindow(user, "mcpshowcase-" + target + "-monthly");
    }
  };

  const sub = user?.subscription;

  const onCancelConfirmed = () => {
    setCancelLoading(true);
    dispatch(
      cancelSubscription(
        intl,
        () => window.location.reload(),
        () => {
          setCancelLoading(false);
          setPendingCancel(false);
        }
      )
    );
  };

  return (
    <ChatbotLayout contentPanelClassName="subscription_content_panel">
      <div className="subscription_page_body">
        <h3>
          <Message id="subscription" />
        </h3>
        {sub ? (
        <>
          <div className="summary">
            <div>
              <LineChartIcon color="currentColor" /> <Message id="plan" />:{" "}
              <div className="value">{capitalize(sub.plan)}</div>
            </div>

            {sub.plan !== "free" && (
              <>
                <div>
                  <CreditCardIcon color="currentColor" />{" "}
                  <Message id="billing_cycle" />:{" "}
                  <div className="value">
                    <Message id={sub.period} />
                  </div>
                </div>
                {sub.period !== "lifetime" && (
                    <div>
                      <CalendarIcon color="currentColor" />{" "}
                      {sub.trialInProgress || sub.daysLeft === 0 ? (
                        <Message
                          id="status_trial"
                          values={{
                            date: dayjs(sub.trialDateEnded).format("DD MMM"),
                          }}
                        />
                      ) : (
                        <Message
                          id="status_paid"
                          values={{
                            date: dayjs(sub.paidDateEnded).format("DD MMM"),
                          }}
                        />
                      )}
                    </div>
                  )}
              </>
            )}
            {sub.dateCanceled ? (
              <div className="subscription_canceled_notice">
                <Message
                  id="subscription_canceled_notice"
                  values={{
                    date: dayjs(sub.paidDateEnded).format("DD MMM YYYY"),
                  }}
                />
              </div>
            ) : (sub.trialInProgress || sub.paid || sub.daysLeft === 0) ? (
              <div>
                <div className="upgrade_section">
                  <div className="upgrade_section__title">
                    <Message id="change_plan" />
                  </div>
                  <div className="upgrade_options_container">
                    {upgradeOptions.map((option) => {
                      const isCurrent = option === sub.plan;
                      const otherActionId = sub.trialInProgress
                        ? "plan_action_switch"
                        : sub.paid
                          ? "plan_action_change"
                          : "plan_action_subscribe";
                      return (
                        <div
                          key={option}
                          className={
                            "upgrade_option " + (isCurrent ? "current" : "")
                          }
                        >
                          <div className="option_title">
                            {isCurrent && <CheckIcon color="currentColor" />}
                            {label(intl, "plan_" + option)}
                          </div>
                          <div className="option_action">
                            {option === inProgressPlan ? (
                              <Spinner />
                            ) : isCurrent ? (
                              sub.paid ? (
                                <span className="current_plan_badge">
                                  <Message id="current_plan" />
                                </span>
                              ) : (
                                <button
                                  className="btn bg-gradient-primary btn-sm"
                                  onClick={() =>
                                    window.showPaymentWindow(
                                      user,
                                      "mcpshowcase-" + option + "-monthly"
                                    )
                                  }
                                >
                                  <Message id="add_card" />
                                </button>
                              )
                            ) : (
                              <button
                                className="btn btn-outline-dark btn-sm"
                                onClick={() => setPendingUpgrade(option)}
                              >
                                <Message id={otherActionId} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="compare_plans">
                    <a href="/p/pricing" target="_blank">
                      <Message id="change_plan_compare" />
                    </a>
                  </div>
                </div>
                {sub.paid && (
                  <div className="cancel_subscription_link">
                    <button
                      className="btn no-btn"
                      onClick={() => setPendingCancel(true)}
                    >
                      <Message id="cancel_subscription" />
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <PlanLimits />
        </>
      ) : (
        <div className="spinner_container">
          <Spinner />
        </div>
      )}
      {orders?.length > 0 && (
        <div className="subscriptio_section orders_panel ">
          <h3>
            <Message id="orders" />
          </h3>
          {orders.map((order) => {
            return (
              <div className="order" key={order.uid}>
                <div>
                  <CalendarIcon color="currentColor" />{" "}
                  {dayjs(order.fsDate).format("DD MMM")}
                </div>
                <div>{order.totalDisplay}</div>
                <div>
                  {order.plan} / <Message id={order.period} />
                </div>
                <div>
                  <a
                    href={order.fsInvoiceUrl + "/pdf"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Message id="invoice" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
        {pendingUpgrade && (
          <ConfirmUpgradeDialog
            planKey={pendingUpgrade}
            onConfirm={onUpgradeConfirmed}
            onCancel={() => setPendingUpgrade(null)}
          />
        )}
        {pendingCancel && (
          <CancelSubscriptionDialog
            date={dayjs(sub.paidDateEnded).format("DD MMM YYYY")}
            loading={cancelLoading}
            onConfirm={onCancelConfirmed}
            onCancel={() => setPendingCancel(false)}
          />
        )}
      </div>
    </ChatbotLayout>
  );
};
export default SubscriptionPage;
